// src/features/admin-options/components/PreferencesTab.tsx
//
// «Mis preferencias» (mockup 13, first tab only, wave 1 skeleton): language,
// default landing page, default scope, table density. Read/write through the
// preferences store (src/providers/preferencesProvider.tsx) — the other two
// tabs of mockup 13 (Opciones del hotel, Opciones de Roommatik) are phase 2
// on the API side and are not built here.
import React from 'react';
import { Box, Card, CardContent, MenuItem, TextField, Typography, Alert } from '@mui/material';
import { TuneRounded as PreferencesIcon } from '@mui/icons-material';
import { useTranslate } from 'react-admin';
import { usePreferences, type LandingPage, type TableDensity } from '@/providers/preferencesProvider';
import { getVisibleNavGroups, resolveStartRoute } from '@/layouts/menuStructure';
import { getAssignedGroups, getAssignedHotels, getCurrentHotel, getCurrentUser } from '@/utils/auth';

const LANDING_PAGES: LandingPage[] = ['arrivals', 'reservations', 'summary'];

/** A user bound to one hotel has no scope to choose («Todos los hoteles» would mean that one hotel). */
const hasSingleHotel = (): boolean => {
  const role = getCurrentUser()?.role;
  if (role === 'SuperAdmin' || role === 'Admin' || getAssignedGroups().length > 0) return false;
  const hotels = new Set([...getAssignedHotels(), getCurrentHotel()].filter(Boolean));
  return hotels.size <= 1;
};

export const PreferencesTab: React.FC = () => {
  const translate = useTranslate();
  const { preferences, setPreferences, source } = usePreferences();
  // Only the start pages this user can open (a Receptionist cannot see
  // Resumen); the value shown is the page "/" actually opens.
  const visibleGroups = getVisibleNavGroups(false);
  const landingOptions = LANDING_PAGES.filter((page) => resolveStartRoute(`/${page}`, visibleGroups) === `/${page}`);
  const landingValue = landingOptions.includes(preferences.landingPage) ? preferences.landingPage : landingOptions[0] ?? '';

  return (
    <Box sx={{ maxWidth: 520 }}>
      {/* Header — icon + heading + one-line description, shared across every admin-options tab */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <PreferencesIcon color="primary" />
        <Typography variant="h6">{translate('preferences.title')}</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {translate('preferences.subtitle')}
      </Typography>

      <Card variant="outlined">
        <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {source === 'local' && (
            <Alert severity="info" variant="outlined">
              {translate('preferences.loadError')}
            </Alert>
          )}

          <TextField
            select
            size="small"
            label={translate('preferences.fields.language')}
            value={preferences.language}
            onChange={(e) => setPreferences({ language: e.target.value as 'es' | 'en' })}
          >
            <MenuItem value="es">Español</MenuItem>
            <MenuItem value="en">English</MenuItem>
          </TextField>

          <TextField
            select
            size="small"
            label={translate('preferences.fields.landingPage')}
            value={landingValue}
            onChange={(e) => setPreferences({ landingPage: e.target.value as LandingPage })}
          >
            {landingOptions.map((page) => (
              <MenuItem key={page} value={page}>
                {translate(`preferences.landingPageOptions.${page}`)}
              </MenuItem>
            ))}
          </TextField>

          {!hasSingleHotel() && (
            <TextField
              select
              size="small"
              label={translate('preferences.fields.defaultScope')}
              value={preferences.scope.hotelIds === null ? 'allHotels' : 'remembered'}
              onChange={(e) =>
                setPreferences({
                  scope: {
                    ...preferences.scope,
                    hotelIds: e.target.value === 'allHotels' ? null : preferences.scope.hotelIds,
                  },
                })
              }
            >
              <MenuItem value="remembered">{translate('preferences.defaultScopeOptions.remembered')}</MenuItem>
              <MenuItem value="allHotels">{translate('preferences.defaultScopeOptions.allHotels')}</MenuItem>
            </TextField>
          )}

          <TextField
            select
            size="small"
            label={translate('preferences.fields.tableDensity')}
            value={preferences.tableDensity}
            onChange={(e) => setPreferences({ tableDensity: e.target.value as TableDensity })}
          >
            <MenuItem value="comfortable">{translate('preferences.tableDensityOptions.comfortable')}</MenuItem>
            <MenuItem value="compact">{translate('preferences.tableDensityOptions.compact')}</MenuItem>
          </TextField>
        </CardContent>
      </Card>
    </Box>
  );
};

export default PreferencesTab;
